import { Box, Card, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SubscriptionPlan } from "../../hooks/useSubscriptionPlans";

interface PlanPickerProps {
  plans: SubscriptionPlan[] | null;
  failed: boolean;
  selectedPlanId: string | null;
  onSelect: (planId: string) => void;
}

/** Default to the first term. Pre-selecting the most expensive one would be a dark pattern. */
export const defaultPlanId = (plans: SubscriptionPlan[] | null): string | null =>
  plans && plans.length > 0 ? plans[0].id : null;

/**
 * The care-plan terms on sale, with a way to pick one.
 *
 * One component for every place a patient can start a plan — the subscription dialog and the
 * Billing page. Billing used to show a single price (the deployment's first, "advertised" plan)
 * while the subscription dialog listed every term, so the same patient saw two different offers
 * depending on where they clicked.
 *
 * Every price, currency and renewal fact comes from Stripe via /subscription/plans: the page never
 * states a number it did not read from the same place the charge comes from.
 */
export default function PlanPicker({ plans, failed, selectedPlanId, onSelect }: PlanPickerProps) {
  if (failed) {
    return (
      <Typography variant="body1" sx={{ color: "error.main", textAlign: "center", mb: 2 }}>
        We could not load pricing just now. Please refresh, or contact us if this keeps happening.
      </Typography>
    );
  }

  if (!plans) {
    return (
      <Typography variant="body1" sx={{ color: "text.secondary", textAlign: "center", mb: 2 }}>
        Loading pricing…
      </Typography>
    );
  }

  if (plans.length === 0) {
    return (
      <Typography variant="body1" sx={{ color: "text.secondary", textAlign: "center", mb: 2 }}>
        No plans are available right now.
      </Typography>
    );
  }

  // Single-plan deployments keep the shape they have always had.
  if (plans.length === 1) {
    const [plan] = plans;
    return (
      <>
        <Typography variant="h4" component="div" sx={{ textAlign: "center", mb: 2 }}>
          ${plan.price.toFixed(2)}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", textAlign: "center", mb: 3 }}>
          {plan.recurring
            ? `Renews every ${plan.months} month${plan.months > 1 ? "s" : ""}. Cancel anytime.`
            : "One-time payment. Does not renew."}
        </Typography>
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
      {plans.map((plan) => {
        const selected = plan.id === selectedPlanId;
        return (
          <Card
            key={plan.id}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            onClick={() => onSelect(plan.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(plan.id);
              }
            }}
            variant="outlined"
            sx={(theme) => ({
              cursor: "pointer",
              p: 2,
              textAlign: "left",
              borderRadius: 2,
              borderWidth: 2,
              borderStyle: "solid",
              borderColor: selected ? "primary.main" : "divider",
              // A tint of the brand colour, not the theme's grey "selected" fill — the selected
              // plan should read as chosen, not as disabled.
              backgroundColor: selected ? alpha(theme.palette.primary.main, 0.08) : "background.paper",
              transition: "border-color .15s, background-color .15s, box-shadow .15s",
              "&:hover": { borderColor: selected ? "primary.main" : "text.disabled", boxShadow: theme.shadows[1] },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 2,
              },
            })}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                {plan.label}
                {plan.badge && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={(theme) => ({
                      ml: 1,
                      px: 1,
                      py: 0.25,
                      borderRadius: 999,
                      fontWeight: 700,
                      color: "success.dark",
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                      verticalAlign: "middle",
                    })}
                  >
                    {plan.badge}
                  </Typography>
                )}
              </Typography>
              <Typography variant="h6" component="div">
                ${plan.price.toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {plan.months > 1 && `$${plan.pricePerMonth.toFixed(2)} per month · `}
              {plan.recurring
                ? `renews every ${plan.months} month${plan.months > 1 ? "s" : ""}`
                : "one-time — does not renew"}
            </Typography>
          </Card>
        );
      })}
    </Box>
  );
}
